import React, { ForwardedRef, useState, useEffect, useRef } from 'react';
import { DataVizType } from '../../types';
import {
  StackedBarChart,
  StackedBarChartOptions,
  LineChart,
  LineChartOptions,
  SlopeChartOptions,
  SlopeChart,
} from '../../design/charts';
import { ChartData, formatScaleValue, formatTooltipValue } from '../data/common';
import { Legend } from '../../design/charts/legend';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';
import { useScrollbarHeight } from '../../design/charts/useScrollbarHeight.hook';
import { CHART_TITLE_HEIGHT } from '../../design/charts/constants';
import { getChartHeight } from '../../design/charts/utils';

type Props = {
  series: ChartData;
  summary?: ChartData;
  currency: string;
  displayAs: DataVizType;
  trackTooltipEvent: () => void;
  chartFullscreen?: boolean;
};

const buildMainChart = (
  series: ChartData,
  displayAs: DataVizType,
  currency: string,
  trackTooltipEvent: () => void,
  isMobile: boolean,
  isChartFullView: boolean,
  chartHeightOffset = 0
): JSX.Element => {
  const { dataSets, totalsDataSet, dataLabels, yColumn } = series;
  const compactView = dataLabels.length >= 26;
  const chartData = { labels: dataLabels, sets: dataSets, summary: totalsDataSet };
  const scaleFormatter = (value: number) => formatScaleValue(value, { yColumn });
  const tooltipFormatter = (value: number) => formatTooltipValue(value, { yColumn, currency });

  if (yColumn.canAggregate && displayAs !== DataVizType.lineChart) {
    const stackedChartOptions: StackedBarChartOptions = {
      yScale: { stacked: true, formatter: scaleFormatter },
      tooltip: { valueFormatter: tooltipFormatter },
      data: chartData,
      compactView,
    };
    return (
      <StackedBarChart
        options={stackedChartOptions}
        trackTooltipEvent={trackTooltipEvent}
        isMobile={isMobile}
        isChartFullView={isChartFullView}
        chartHeightOffset={chartHeightOffset}
      />
    );
  }

  const lineChartOptions: LineChartOptions = {
    yScale: { formatter: scaleFormatter },
    tooltip: { valueFormatter: tooltipFormatter },
    data: chartData,
    compactView,
  };
  return (
    <LineChart
      options={lineChartOptions}
      trackTooltipEvent={trackTooltipEvent}
      isMobile={isMobile}
      isChartFullView={isChartFullView}
      chartHeightOffset={chartHeightOffset}
    />
  );
};

const buildSummaryChart = (
  summary: ChartData | undefined,
  currency: string,
  trackTooltipEvent: () => void,
  isMobile: boolean,
  isChartFullView: boolean,
  chartHeightOffset = 0
): JSX.Element | null => {
  if (!summary) {
    return null;
  }

  const { dataSets, totalsDataSet, dataLabels, yColumn } = summary;
  const summaryData = { labels: dataLabels, sets: dataSets, summary: totalsDataSet };
  const summaryScaleFormatter = (value: number) => formatScaleValue(value, { yColumn });
  const summaryTooltipFormatter = (value: number) => formatTooltipValue(value, { yColumn, currency });

  if (yColumn.canAggregate) {
    const summaryStackedChartOptions: StackedBarChartOptions = {
      yScale: { stacked: true, formatter: summaryScaleFormatter },
      tooltip: { valueFormatter: summaryTooltipFormatter },
      data: summaryData,
    };
    return (
      <StackedBarChart
        options={summaryStackedChartOptions}
        trackTooltipEvent={trackTooltipEvent}
        isMobile={isMobile}
        isChartFullView={isChartFullView}
        chartHeightOffset={chartHeightOffset}
      />
    );
  }

  const summarySlopeOptions: SlopeChartOptions = {
    yScale: { formatter: summaryScaleFormatter },
    tooltip: { valueFormatter: summaryTooltipFormatter },
    data: summaryData,
  };
  return (
    <SlopeChart
      options={summarySlopeOptions}
      trackTooltipEvent={trackTooltipEvent}
      isMobile={isMobile}
      isChartFullView={isChartFullView}
      chartHeightOffset={chartHeightOffset}
    />
  );
};

const TimeSeriesChart = React.forwardRef<HTMLDivElement, Props>(
  (
    { series, summary, currency, displayAs, trackTooltipEvent, chartFullscreen }: Props,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { totalsDataSet, yColumn, updatedYColumn } = series;

    const includeTotals = totalsDataSet !== undefined;
    const totalsLabel = includeTotals ? `Market ${updatedYColumn?.label || yColumn.label}` : undefined;
    const totalsChangeLabel = includeTotals ? 'Change vs same period prior year' : undefined;
    const { isMobile } = useMobile();
    const [isLandscape, setIsLandscape] = useState(
      typeof window !== 'undefined' && window.innerWidth > window.innerHeight
    );
    const chartOverflowContainerRef = useRef<HTMLDivElement>(null);
    const scrollbarHeight = useScrollbarHeight(chartOverflowContainerRef);
    const chartHeightOffset = isMobile ? CHART_TITLE_HEIGHT + scrollbarHeight : 0;

    useEffect(() => {
      const handleOrientationChange = () => {
        setIsLandscape(window.innerWidth > window.innerHeight);
      };

      window.addEventListener('resize', handleOrientationChange);
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('resize', handleOrientationChange);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }, []);

    const isChartFullView = isLandscape || chartFullscreen;

    const Chart = buildMainChart(
      series,
      displayAs,
      currency,
      trackTooltipEvent,
      isMobile,
      isChartFullView,
      chartHeightOffset
    );
    const SummaryChart = buildSummaryChart(
      summary,
      currency,
      trackTooltipEvent,
      isMobile,
      isChartFullView,
      chartHeightOffset
    );

    //checks the hasData prop which is ommited in DP
    const isSeriesDataEmpty = 'hasData' in series ? !series.hasData : false;
    const isSummaryDataEmpty = summary && ('hasData' in summary ? !summary.hasData : false);

    if (isSeriesDataEmpty && isSummaryDataEmpty) {
      return <EmptyState height="500px" />;
    }

    return (
      <div ref={ref} className="relative overflow-x-auto w-full">
        <div
          className={classNames('w-full flex flex-rows items-stretch gap-2', {
            'flex-column-reverse': isMobile,
          })}
        >
          <Legend data={series.dataSets} totalsLabel={totalsLabel} totalsChangeLabel={totalsChangeLabel} />
          {isMobile ? (
            <div
              ref={chartOverflowContainerRef}
              className="flex charts gap-2 overflow-y-auto overflow-x-auto"
              style={{ height: getChartHeight(isMobile, isChartFullView, false, chartHeightOffset) }}
            >
              {SummaryChart && <div className="relative border-r border-light pr-4 min-w-200">{SummaryChart}</div>}
              <div className="relative flex-grow min-w-800">{Chart}</div>
            </div>
          ) : (
            <>
              {SummaryChart && <div className="relative border-r border-light pr-4">{SummaryChart}</div>}
              <div className="relative flex-grow overflow-x-auto overflow-y-hidden">{Chart}</div>
            </>
          )}
        </div>
      </div>
    );
  }
);

export { TimeSeriesChart };
